import React, { useState, useEffect } from 'react';
import './FabMenu.css';
import { FaPlus, FaGoogle, FaLinkedin } from 'react-icons/fa';
import { SiReaddotcv, SiKakaotalk } from 'react-icons/si';
import { trackInteraction } from '../api/interactions';

const KAKAO_APP_KEY = '736aee2c28445c278db4eaec8dc63472';

const FabMenu = ({ cardData, shortId, idToken }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [kakaoLoaded, setKakaoLoaded] = useState(false);

  // 카카오 SDK 동적 로드
  useEffect(() => {
    if (window.Kakao) {
      if (!window.Kakao.isInitialized()) window.Kakao.init(KAKAO_APP_KEY);
      setKakaoLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://t1.kakaocdn.net/kakao_js_sdk/2.6.0/kakao.min.js';
    script.integrity = 'sha384-6MFdIr0zOira1CHQkedUqJVql0YtcZA1P0nbPrQYJXVJZUkTk/oX4U9GhUIs3/z8';
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      if (!window.Kakao.isInitialized()) window.Kakao.init(KAKAO_APP_KEY);
      setKakaoLoaded(true);
    };
    document.head.appendChild(script);
    return () => { if (document.head.contains(script)) document.head.removeChild(script); };
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // 1. 구글 주소록 (vCard 다운로드 방식)
  // 최신 스마트폰/PC에서 이 파일을 열면 자동으로 "연락처 저장" 화면이 뜹니다.
  const handleGoogleAdd = async () => {
    const contact = {
      name: cardData.details.name,
      title: cardData.details.title,
      phone: cardData.details.phone,
      email: cardData.details.email,
      company: cardData.details.company,
      website: cardData.details.website,
      address: cardData.details.address,
    };

    // vCard 포맷 생성
    const vCardData = `BEGIN:VCARD
VERSION:3.0
FN:${contact.name}
${contact.title ? `TITLE:${contact.title}` : ''}
TEL;TYPE=CELL:${contact.phone}
EMAIL:${contact.email}
ORG:${contact.company}
${contact.website ? `URL:${contact.website}` : ''}
${contact.address ? `ADR;TYPE=WORK:;;${contact.address}` : ''}
END:VCARD`;

    const blob = new Blob([vCardData], { type: "text/vcard" });
    const url = URL.createObjectURL(blob);
    
    // 가상의 링크를 만들어 다운로드 트리거
    const link = document.createElement('a');
    link.href = url;
    link.download = `${contact.name}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    const token = idToken ? await idToken() : null;
    trackInteraction(shortId, 'vcard_download', token);
  };

  // 카카오톡 공유
  const handleKakaoShare = () => {
    const shareUrl = shortId
      ? `${window.location.origin}/card/${shortId}`
      : window.location.href;

    if (!kakaoLoaded) {
      navigator.clipboard.writeText(shareUrl);
      return;
    }

    const { name, title, company } = cardData?.details || {};
    const desc = [company, title].filter(Boolean).join(' · ');

    try {
      window.Kakao.Share.sendDefault({
        objectType: 'feed',
        content: {
          title: name ? `${name}의 명함` : 'EPIX Card',
          description: desc || 'EPIX Card로 명함을 확인하세요',
          imageUrl: 'https://biz.epix.kr/logo192.png',
          link: {
            webUrl: shareUrl,
            mobileWebUrl: shareUrl,
          },
        },
        buttons: [
          {
            title: '명함 보기',
            link: {
              webUrl: shareUrl,
              mobileWebUrl: shareUrl,
            },
          },
        ],
      });
    } catch {
      navigator.clipboard.writeText(shareUrl);
    }
  };

  // 2. 리멤버 & 3. 링크드인
  // 각 서비스는 "원클릭 추가 API"를 공개적으로 제공하지 않으므로, 프로필 링크로 이동시킵니다.
  const rememberUrl = "https://rememberapp.co.kr"; // 본인의 리멤버 프로필 URL로 변경하세요
  const linkedinUrl = "https://www.linkedin.com/in/your-profile"; // 본인의 링크드인 URL로 변경하세요

  return (
    <div className={`fab-container ${isOpen ? 'active' : ''}`}>
      {/* 메인 토글 버튼 */}
      <button className="fab-btn main-btn" onClick={toggleMenu}>
        <FaPlus />
      </button>

      {/* 3. 링크드인 (새 탭 열기) */}
      <a
        href={linkedinUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="fab-btn sub-btn linkedin"
        data-label="LinkedIn 연결"
        onClick={async () => trackInteraction(shortId, 'linkedin_click', idToken ? await idToken() : null)}
      >
        <FaLinkedin />
      </a>

      {/* 2. 리멤버 (새 탭 열기) */}
      <a
        href={rememberUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="fab-btn sub-btn remember"
        data-label="리멤버 명함 추가"
        onClick={async () => trackInteraction(shortId, 'remember_click', idToken ? await idToken() : null)}
      >
        <SiReaddotcv />
      </a>

      {/* 1. 구글 주소록 (파일 다운로드) */}
      <button
        className="fab-btn sub-btn google"
        onClick={handleGoogleAdd}
        data-label="구글 주소록 저장"
      >
        <FaGoogle />
      </button>

      {/* 0. 카카오톡 공유 */}
      <button
        className="fab-btn sub-btn kakao"
        onClick={handleKakaoShare}
        data-label="카카오톡 공유"
      >
        <SiKakaotalk />
      </button>
    </div>
  );
};

export default FabMenu;